import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Body2,
  Button,
  Caption1,
  Card,
  Divider,
  Link as FluentLink,
  Spinner,
  Subtitle1,
  Subtitle2,
} from '@fluentui/react-components';
import {
  ArrowLeftRegular,
  BrainSparkleRegular,
  CommentRegular,
  PersonRegular,
  SubtractCircleRegular,
} from '@fluentui/react-icons';

import { useDocumentTitle, useSearchParams } from '@shared/hooks';
import { NoPage } from '@shared/pages';

import { AssignFindingDialog } from '../components/finding/AssignFindingDialog';
import { ChangeModeDialog } from '../components/finding/ChangeModeDialog';
import { CommentFindingDialog } from '../components/finding/CommentFindingDialog';
import { DismissFindingDialog } from '../components/finding/DismissFindingDialog';
import { FindingComments } from '../components/finding/FindingComments';
import {
  ConfidenceBadge,
  FindingAssignment,
  FindingDocuments,
  FindingStatusBadge,
  SeverityBadge,
} from '../components/shared';
import { getReviewPath } from '../helpers';
import { ReviewPageScaffold } from '../layouts/ReviewPageScaffold';
import { useReviewStore } from '../state/ReviewStore';

const FindingDetailContent = () => {
  const navigate = useNavigate();
  const { reviewId, findingId } = useParams();
  const { searchParams } = useSearchParams();
  const productId = searchParams.get('productId') ?? undefined;
  const { getReview, acceptChange, assignFinding, dismissFinding, reopenFinding, addComment } = useReviewStore();
  const [isChangeOpen, setIsChangeOpen] = useState(false);
  const [isProposingSolution, setIsProposingSolution] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [isDismissOpen, setIsDismissOpen] = useState(false);
  const [isCommentOpen, setIsCommentOpen] = useState(false);

  useEffect(() => {
    if (!isProposingSolution) return;
    const timeout = window.setTimeout(() => {
      setIsProposingSolution(false);
      setIsChangeOpen(true);
    }, 5000);
    return () => window.clearTimeout(timeout);
  }, [isProposingSolution]);

  const review = reviewId ? getReview(reviewId) : undefined;
  const finding = review?.findings.find((entry) => entry.id === findingId);
  if (!review || !finding) return <NoPage />;

  const change = finding.resolution?.change;
  const isClosed = finding.status === 'resolved' || finding.status === 'dismissed';
  const evidence = (finding.affectedDocuments ?? []).filter((document) => document.excerpt);

  return (
    <>
      <Link to={getReviewPath(review.id, productId)}>
        <Button appearance="subtle" icon={<ArrowLeftRegular />}>
          Findings
        </Button>
      </Link>

      <Card size="large" className="max-w-5xl !p-6 !gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <SeverityBadge severity={finding.severity} />
            <ConfidenceBadge />
            <Body2 className="text-neutralFg-3">{finding.category}</Body2>
          </div>
          <FindingStatusBadge status={finding.status} size="large" />
        </div>

        <div className="flex flex-col gap-3">
          <Subtitle1>{finding.title}</Subtitle1>
          <Body2 className="whitespace-pre-line">{finding.summary}</Body2>
        </div>

        {finding.assignee || finding.dueDate ? <FindingAssignment finding={finding} /> : null}
        {finding.dismissReason ? <Body2 className="text-neutralFg-3">{finding.dismissReason}</Body2> : null}

        <div className="flex flex-wrap items-center gap-3">
          {change ? (
            <Button
              size="medium"
              icon={
                isProposingSolution ? <Spinner size="tiny" aria-label="Preparing solution" /> : <BrainSparkleRegular />
              }
              appearance={isClosed ? 'secondary' : 'primary'}
              disabled={isProposingSolution}
              aria-busy={isProposingSolution}
              onClick={() => (isClosed ? setIsChangeOpen(true) : setIsProposingSolution(true))}
            >
              {isClosed ? 'View change' : 'Propose solution'}
            </Button>
          ) : null}
          {!isClosed ? (
            <>
              <Button
                size="medium"
                appearance="secondary"
                disabled={isProposingSolution}
                icon={<PersonRegular />}
                onClick={() => setIsAssignOpen(true)}
              >
                Assign
              </Button>
              <Button
                size="medium"
                appearance="secondary"
                disabled={isProposingSolution}
                icon={<SubtractCircleRegular />}
                onClick={() => setIsDismissOpen(true)}
              >
                Not applicable
              </Button>
            </>
          ) : (
            <Button size="medium" appearance="secondary" onClick={() => reopenFinding(review.id, finding.id)}>
              Reopen
            </Button>
          )}
          <Button
            size="medium"
            appearance="secondary"
            disabled={isProposingSolution}
            icon={<CommentRegular />}
            onClick={() => setIsCommentOpen(true)}
          >
            Comment
          </Button>
        </div>

        <Divider />

        {finding.affectedDocuments?.length ? (
          <div className="flex flex-col gap-3">
            <Subtitle2>Documents</Subtitle2>
            <FindingDocuments documents={finding.affectedDocuments} />
          </div>
        ) : null}

        {finding.reviewBasis?.length ? (
          <div className="flex flex-col gap-2">
            <Subtitle2>Regulations and standards</Subtitle2>
            {finding.reviewBasis.map((basis) => (
              <Body2 key={basis.id}>
                {basis.sourceUrl ? (
                  <FluentLink href={basis.sourceUrl} target="_blank" rel="noopener noreferrer">
                    {basis.reference ? `${basis.label}, ${basis.reference}` : basis.label}
                  </FluentLink>
                ) : (
                  <FluentLink as="span">
                    {basis.reference ? `${basis.label}, ${basis.reference}` : basis.label}
                  </FluentLink>
                )}
              </Body2>
            ))}
          </div>
        ) : null}

        {evidence.length || finding.whyItMatters || (!change && finding.resolution) ? (
          <Accordion collapsible>
            <AccordionItem value="evidence">
              <AccordionHeader size="large">Supporting evidence</AccordionHeader>
              <AccordionPanel className="!mx-0">
                <div className="flex flex-col gap-5 py-2">
                  {evidence.map((document, index) => (
                    <div key={index} className="flex flex-col gap-2">
                      <div className="flex flex-col gap-1">
                        <FindingDocuments documents={[document]} />
                        {document.location ? (
                          <Caption1 className="text-neutralFg-3">{document.location}</Caption1>
                        ) : null}
                      </div>
                      <Body2 className="text-neutralFg-3">{document.excerpt}</Body2>
                    </div>
                  ))}
                  {finding.whyItMatters ? <Body2 className="whitespace-pre-line">{finding.whyItMatters}</Body2> : null}
                  {!change && finding.resolution ? (
                    <div className="flex flex-col gap-2">
                      <Subtitle2>Suggested resolution</Subtitle2>
                      <Body2>{finding.resolution.summary}</Body2>
                    </div>
                  ) : null}
                </div>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        ) : null}

        <FindingComments comments={finding.comments} />
      </Card>

      <ChangeModeDialog
        change={isChangeOpen ? change : undefined}
        explanation={finding.resolution?.summary}
        isAccepted={finding.status === 'resolved'}
        onAccept={() => {
          acceptChange(review.id, finding.id);
          setIsChangeOpen(false);
          navigate(getReviewPath(review.id, productId));
        }}
        onClose={() => setIsChangeOpen(false)}
      />

      <AssignFindingDialog
        open={isAssignOpen}
        assigneeId={finding.assigneeId}
        dueDate={finding.dueDate}
        onAssign={(assignee, dueDate, assigneeId) => {
          assignFinding(review.id, finding.id, assignee, dueDate, assigneeId);
          setIsAssignOpen(false);
        }}
        onClose={() => setIsAssignOpen(false)}
      />

      <DismissFindingDialog
        open={isDismissOpen}
        onDismissFinding={(reason) => {
          dismissFinding(review.id, finding.id, reason);
          setIsDismissOpen(false);
        }}
        onClose={() => setIsDismissOpen(false)}
      />

      <CommentFindingDialog
        open={isCommentOpen}
        onComment={(text) => {
          addComment(review.id, finding.id, text);
          setIsCommentOpen(false);
        }}
        onClose={() => setIsCommentOpen(false)}
      />
    </>
  );
};

const FindingDetailPage = () => {
  const { reviewId, findingId } = useParams();
  const { searchParams } = useSearchParams();
  const productId = searchParams.get('productId') ?? undefined;
  const { getReview } = useReviewStore();
  const review = reviewId ? getReview(reviewId) : undefined;
  const finding = review?.findings.find((entry) => entry.id === findingId);

  useDocumentTitle(finding?.title ?? 'Finding');

  const breadcrumbTail = review
    ? [{ name: review.name, href: getReviewPath(review.id, productId) }, { name: finding?.title ?? 'Finding' }]
    : [];

  return (
    <ReviewPageScaffold breadcrumbTail={breadcrumbTail}>
      <FindingDetailContent key={`${reviewId}/${findingId}`} />
    </ReviewPageScaffold>
  );
};

export default FindingDetailPage;
